// src/features/services/sales/components/CreateSalesOrderPanel.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Form panel to create a new sales order (IoT provisioning pipeline).
// PAC Glass design system. Zero-prop — data via useSalesOrders hook.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSalesOrders } from '../hooks/useSalesOrders';
import { fetchInventoryApi, QK_INVENTORY, type SkuInventory } from '../api/productionOrderApi';
import type { CreateSalesOrderPayload, Sku, WifiNetwork } from '../api/salesOrderApi';

const MAX_WIFI_NETWORKS = 10;

const INPUT =
  'mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:ring-2 focus:ring-emerald-500';
const SELECT = INPUT;
const LABEL = 'block text-xs text-neutral-400 uppercase tracking-wider';

const emptyNetwork = (): WifiNetwork => ({ ssid: '', pass: '' });

export default function CreateSalesOrderPanel() {
  const { skus, create } = useSalesOrders();
  const { data: inventoryData } = useQuery<SkuInventory, Error>({
    queryKey: [...QK_INVENTORY],
    queryFn: () => fetchInventoryApi(),
    staleTime: 30_000,
  });
  const inventory: SkuInventory = inventoryData ?? {};

  // ── form state ──────────────────────────────────────────────────────────
  const [sku, setSku] = useState('');
  const [wifiNetworks, setWifiNetworks] = useState<WifiNetwork[]>([emptyNetwork()]);
  const [quantity, setQuantity] = useState(1);
  const [labelsRaw, setLabelsRaw] = useState('');  // comma-separated

  const skuList: Sku[] = skus.data?.items ?? [];

  const labels = useMemo(() => {
    if (!labelsRaw.trim()) return undefined;
    return labelsRaw.split(',').map((s) => s.trim()).filter(Boolean);
  }, [labelsRaw]);

  const hasValidNetwork = wifiNetworks.some((n) => n.ssid.trim() && n.pass.trim());

  const canSubmit =
    sku.trim() &&
    hasValidNetwork &&
    quantity >= 1 &&
    !create.isPending;

  const updateNetwork = (i: number, field: keyof WifiNetwork, value: string) => {
    setWifiNetworks((prev) => prev.map((n, idx) => (idx === i ? { ...n, [field]: value } : n)));
  };
  const addNetwork = () => {
    if (wifiNetworks.length < MAX_WIFI_NETWORKS) setWifiNetworks((prev) => [...prev, emptyNetwork()]);
  };
  const removeNetwork = (i: number) => {
    if (wifiNetworks.length > 1) setWifiNetworks((prev) => prev.filter((_, idx) => idx !== i));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const networks = wifiNetworks
      .filter((n) => n.ssid.trim())
      .map((n) => ({ ssid: n.ssid.trim(), pass: n.pass.trim() }));

    const payload: CreateSalesOrderPayload = {
      sku: sku.trim(),
      wifi_networks: networks,
      quantity,
      labels,
    };

    await create.mutateAsync(payload);

    // reset
    setLabelsRaw('');
    setQuantity(1);
  };

  // ── last created order (success feedback) ──────────────────────────────
  const lastOrder = create.data;

  return (
    <div className="pac-glass rounded-lg p-5 space-y-5">
      <h2 className="text-base font-semibold text-neutral-100 tracking-tight">
        Create Sales Order
      </h2>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* ── Row 1: SKU + Quantity ─────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className={LABEL + ' sm:col-span-2'}>
            SKU
            <select value={sku} onChange={(e) => setSku(e.target.value)} className={SELECT}>
              <option value="">— select SKU —</option>
              {skuList.map((s) => {
                const qty = inventory[s.sku] ?? 0;
                return (
                  <option key={s.sku} value={s.sku} className="bg-neutral-900">
                    {s.label || s.sku} [{qty}]
                  </option>
                );
              })}
            </select>
          </label>

          <label className={LABEL}>
            Quantity
            <input
              type="number"
              min={1}
              max={100}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className={INPUT}
            />
          </label>
        </div>

        {/* ── WiFi Networks ──────────────────────────────────────────── */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={LABEL}>WiFi Networks ({wifiNetworks.length}/{MAX_WIFI_NETWORKS})</span>
            {wifiNetworks.length < MAX_WIFI_NETWORKS && (
              <button
                type="button"
                onClick={addNetwork}
                className="text-[10px] text-emerald-500 hover:text-emerald-400 transition-colors"
              >
                + Add Network
              </button>
            )}
          </div>
          {wifiNetworks.map((net, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <input
                value={net.ssid}
                onChange={(e) => updateNetwork(i, 'ssid', e.target.value)}
                placeholder={i === 0 ? 'Primary SSID' : `Network ${i + 1} SSID`}
                maxLength={32}
                className={INPUT}
              />
              <input
                type="password"
                value={net.pass}
                onChange={(e) => updateNetwork(i, 'pass', e.target.value)}
                placeholder="Password"
                className={INPUT}
              />
              <button
                type="button"
                onClick={() => removeNetwork(i)}
                disabled={wifiNetworks.length <= 1}
                className="mt-1 p-2 text-neutral-600 hover:text-rose-400 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                title="Remove"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {/* ── Row 3: Labels (optional) ──────────────────────────────────── */}
        {quantity > 1 && (
          <label className={LABEL}>
            Labels
            <input
              value={labelsRaw}
              onChange={(e) => setLabelsRaw(e.target.value)}
              placeholder={`Office A, Lab B, Lobby C  (${quantity} items — defaults to #1 ${sku || 'sku'}, #2 …)`}
              className={INPUT}
            />
            <span className="text-[10px] text-neutral-600 mt-0.5 block">
              Comma-separated. Leave empty for auto-numbered labels.
            </span>
          </label>
        )}

        {/* ── Actions ───────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={!canSubmit}
            className="pac-glass-btn px-5 py-2 rounded text-sm font-medium border border-neutral-700
                       text-neutral-200 hover:border-emerald-500 hover:text-emerald-400
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {create.isPending ? 'Creating…' : 'Create Order'}
          </button>

          {create.isError && (
            <span className="text-xs text-rose-400">
              {(create.error as Error)?.message ?? 'Failed to create order'}
            </span>
          )}
        </div>
      </form>

      {/* ── Success feedback ──────────────────────────────────────────── */}
      {lastOrder && (
        <div className="pac-glass-raised rounded p-4 space-y-2 border border-emerald-900/40">
          <p className="text-sm text-emerald-400 font-medium">
            Order created — {lastOrder.order.serial_number}
          </p>
          <div className="text-xs text-neutral-400 space-y-0.5">
            <p>SKU: {lastOrder.order.sku} · Qty: {lastOrder.order.quantity}</p>
            <p>Scope: {lastOrder.order.order_scope_key}</p>
          </div>
          {lastOrder.line_items.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
                Line Items ({lastOrder.line_items.length})
              </p>
              <div className="space-y-1">
                {lastOrder.line_items.map((li) => (
                  <div key={li.scope_key} className="flex items-center gap-2 text-xs text-neutral-300">
                    <span className="text-emerald-500 font-mono">{li.serial_number}</span>
                    <span className="text-neutral-500">—</span>
                    <span>{li.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
